import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Zap, CheckCircle2, XCircle, AlertTriangle, RefreshCw, ExternalLink } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api, { errMsg } from "@/lib/api";

const STATUS_STYLES = {
    connected:      { icon: CheckCircle2, cls: "text-[#15803D] bg-green-50 border-green-200",   label: "Connected" },
    configured:     { icon: CheckCircle2, cls: "text-[#15803D] bg-green-50 border-green-200",   label: "Configured" },
    not_connected:  { icon: XCircle,      cls: "text-[#64748B] bg-[#F5F7FB] border-[#E5E9F2]", label: "Not connected" },
    not_configured: { icon: XCircle,      cls: "text-[#64748B] bg-[#F5F7FB] border-[#E5E9F2]", label: "Not configured" },
    error:          { icon: AlertTriangle, cls: "text-[#DC2626] bg-red-50 border-red-200",      label: "Error" },
};

function StatusPill({ status, testId }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.not_connected;
    const Icon = s.icon;
    return (
        <span className={"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold " + s.cls}
            data-testid={testId}>
            <Icon size={12} /> {s.label}
        </span>
    );
}

function Card({ title, subtitle, docsHref, status, statusTestId, children, badge }) {
    return (
        <div className="ddi-card ddi-shadow-sm p-6 mb-5">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-[#123B5D] tracking-tight">{title}</h2>
                        {badge}
                    </div>
                    {subtitle && <p className="text-sm text-[#64748B] mt-0.5">{subtitle}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <StatusPill status={status} testId={statusTestId} />
                    {docsHref && (
                        <a href={docsHref} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                            Docs <ExternalLink size={11} />
                        </a>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="ddi-input-label">{label}</label>
            {children}
            {hint && <div className="text-[11px] text-[#64748B] mt-1">{hint}</div>}
        </div>
    );
}

export default function SuperAdminMarketing() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState({});

    // Local drafts (persist unsaved edits)
    const [meta, setMeta] = useState({ pixel_id: "", access_token: "", test_event_code: "", enabled: false });
    const [ga4, setGA4] = useState({ measurement_id: "", api_secret: "", enabled: false });
    const [ads, setAds] = useState({ conversion_id: "", labels: { signup: "", advance_payment: "", full_payment: "" }, enabled: false });

    useEffect(() => { load(); }, []);
    async function load() {
        try {
            const r = await api.get("/super-admin/marketing");
            setCfg(r.data);
            setMeta({ pixel_id: r.data.meta.pixel_id || "", access_token: "",
                      test_event_code: r.data.meta.test_event_code || "", enabled: !!r.data.meta.enabled });
            setGA4({ measurement_id: r.data.ga4.measurement_id || "", api_secret: "", enabled: !!r.data.ga4.enabled });
            setAds({ conversion_id: r.data.google_ads.conversion_id || "",
                     labels: r.data.google_ads.labels || { signup: "", advance_payment: "", full_payment: "" },
                     enabled: !!r.data.google_ads.enabled });
        } catch (e) { toast.error(errMsg(e, "Failed to load config")); }
    }

    async function save(section, payload) {
        setBusy((b) => ({ ...b, [`save-${section}`]: true }));
        try {
            const r = await api.put(`/super-admin/marketing/${section}`, payload);
            setCfg(r.data);
            toast.success(`${section} saved`);
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setBusy((b) => ({ ...b, [`save-${section}`]: false })); }
    }

    async function verify(section) {
        setBusy((b) => ({ ...b, [`ver-${section}`]: true }));
        try {
            const r = await api.post(`/super-admin/marketing/${section}/verify`);
            const s = r.data.status;
            setCfg((c) => ({ ...c, [section]: { ...c[section], ...r.data.config } }));
            (s === "connected" || s === "configured" ? toast.success : toast.error)(
                `${section}: ${r.data.message}`
            );
        } catch (e) { toast.error(errMsg(e, "Verify failed")); }
        finally { setBusy((b) => ({ ...b, [`ver-${section}`]: false })); }
    }

    if (!cfg) {
        return (
            <AdminShell>
                <div className="animate-fade-up space-y-4">
                    <div className="ddi-skeleton h-8 w-64" />
                    <div className="ddi-skeleton h-40 w-full" />
                    <div className="ddi-skeleton h-40 w-full" />
                </div>
            </AdminShell>
        );
    }

    return (
        <AdminShell>
            <div className="animate-fade-up" data-testid="marketing-page">
                <div className="ddi-eyebrow text-[#0F766E]">Super Admin · Marketing</div>
                <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                    Marketing &amp; Ads Center
                </h1>
                <p className="mt-2 text-sm text-[#17212B]/70 leading-relaxed max-w-2xl">
                    Connect your Meta, Google Analytics 4, and Google Ads accounts. Credentials are stored server-side and never exposed to the frontend. Every save is audit-logged.
                </p>

                {/* Meta */}
                <div className="mt-8">
                <Card
                    title="Meta (Facebook / Instagram) Ads"
                    subtitle="Pixel + Conversions API — client & server-side tracking."
                    status={cfg.meta.status}
                    statusTestId="marketing-meta-status"
                    docsHref="https://developers.facebook.com/docs/marketing-api/conversions-api"
                    badge={<Zap size={14} className="text-[#B8862B]" />}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Pixel ID">
                            <input className="ddi-input" value={meta.pixel_id}
                                onChange={(e) => setMeta({ ...meta, pixel_id: e.target.value })}
                                placeholder="15-16 digit numeric ID"
                                data-testid="marketing-meta-pixel-id" />
                        </Field>
                        <Field label="Test event code (optional)" hint="From Events Manager → Test events.">
                            <input className="ddi-input" value={meta.test_event_code}
                                onChange={(e) => setMeta({ ...meta, test_event_code: e.target.value })}
                                placeholder="TEST12345"
                                data-testid="marketing-meta-test-code" />
                        </Field>
                        <Field label="CAPI Access Token" hint={cfg.meta.masked_token ? `Saved: ${cfg.meta.masked_token} — leave blank to keep.` : "System user token from Business Settings."}>
                            <input type="password" className="ddi-input" value={meta.access_token}
                                onChange={(e) => setMeta({ ...meta, access_token: e.target.value })}
                                placeholder={cfg.meta.masked_token ? "•••• leave blank to keep existing" : "EAAB…"}
                                data-testid="marketing-meta-token" />
                        </Field>
                        <Field label="Enable pixel injection">
                            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                                <input type="checkbox" checked={meta.enabled}
                                    onChange={(e) => setMeta({ ...meta, enabled: e.target.checked })}
                                    data-testid="marketing-meta-enabled" />
                                <span className="text-sm text-[#123B5D] font-medium">Inject Pixel on public pages</span>
                            </label>
                        </Field>
                    </div>
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <button className="ddi-teal-btn text-sm" disabled={!!busy["save-meta"]}
                            onClick={() => save("meta", meta)}
                            data-testid="marketing-meta-save">
                            <Save size={13} className="mr-1.5" />
                            {busy["save-meta"] ? "Saving…" : "Save"}
                        </button>
                        <button className="ddi-btn-outline text-sm" disabled={!!busy["ver-meta"] || !cfg.meta.configured}
                            onClick={() => verify("meta")}
                            data-testid="marketing-meta-verify">
                            <RefreshCw size={13} className={"mr-1.5 " + (busy["ver-meta"] ? "animate-spin" : "")} />
                            {busy["ver-meta"] ? "Verifying…" : "Save & Verify"}
                        </button>
                    </div>
                    {cfg.meta.last_error && (
                        <div className="ddi-error-text mt-3" data-testid="marketing-meta-error">
                            <AlertTriangle size={13} /> {cfg.meta.last_error}
                        </div>
                    )}
                </Card>
                </div>

                {/* GA4 */}
                <Card
                    title="Google Analytics 4"
                    subtitle="Measurement ID + Measurement Protocol API secret."
                    status={cfg.ga4.status}
                    statusTestId="marketing-ga4-status"
                    docsHref="https://developers.google.com/analytics/devguides/collection/protocol/ga4"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Measurement ID">
                            <input className="ddi-input" value={ga4.measurement_id}
                                onChange={(e) => setGA4({ ...ga4, measurement_id: e.target.value })}
                                placeholder="G-XXXXXXXXXX"
                                data-testid="marketing-ga4-measurement-id" />
                        </Field>
                        <Field label="API secret" hint={cfg.ga4.masked_secret ? `Saved: ${cfg.ga4.masked_secret} — leave blank to keep.` : "From Admin → Data streams → Measurement protocol."}>
                            <input type="password" className="ddi-input" value={ga4.api_secret}
                                onChange={(e) => setGA4({ ...ga4, api_secret: e.target.value })}
                                placeholder={cfg.ga4.masked_secret ? "•••• leave blank to keep existing" : "your-api-secret"}
                                data-testid="marketing-ga4-secret" />
                        </Field>
                        <Field label="Enable gtag injection">
                            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                                <input type="checkbox" checked={ga4.enabled}
                                    onChange={(e) => setGA4({ ...ga4, enabled: e.target.checked })}
                                    data-testid="marketing-ga4-enabled" />
                                <span className="text-sm text-[#123B5D] font-medium">Inject gtag.js on public pages</span>
                            </label>
                        </Field>
                    </div>
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <button className="ddi-teal-btn text-sm" disabled={!!busy["save-ga4"]}
                            onClick={() => save("ga4", ga4)}
                            data-testid="marketing-ga4-save">
                            <Save size={13} className="mr-1.5" />
                            {busy["save-ga4"] ? "Saving…" : "Save"}
                        </button>
                        <button className="ddi-btn-outline text-sm" disabled={!!busy["ver-ga4"] || !cfg.ga4.configured}
                            onClick={() => verify("ga4")}
                            data-testid="marketing-ga4-verify">
                            <RefreshCw size={13} className={"mr-1.5 " + (busy["ver-ga4"] ? "animate-spin" : "")} />
                            {busy["ver-ga4"] ? "Verifying…" : "Save & Verify"}
                        </button>
                    </div>
                    {cfg.ga4.last_error && (
                        <div className="ddi-error-text mt-3" data-testid="marketing-ga4-error">
                            <AlertTriangle size={13} /> {cfg.ga4.last_error}
                        </div>
                    )}
                </Card>

                {/* Google Ads */}
                <Card
                    title="Google Ads"
                    subtitle="Conversion tag + event labels for signup, advance & full payment."
                    status={cfg.google_ads.status}
                    statusTestId="marketing-ads-status"
                    docsHref="https://support.google.com/google-ads/answer/6095821"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Conversion ID">
                            <input className="ddi-input" value={ads.conversion_id}
                                onChange={(e) => setAds({ ...ads, conversion_id: e.target.value })}
                                placeholder="AW-XXXXXXXXX"
                                data-testid="marketing-ads-conversion-id" />
                        </Field>
                        <Field label="Enable Ads tag">
                            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                                <input type="checkbox" checked={ads.enabled}
                                    onChange={(e) => setAds({ ...ads, enabled: e.target.checked })}
                                    data-testid="marketing-ads-enabled" />
                                <span className="text-sm text-[#123B5D] font-medium">Inject gtag conversion tag</span>
                            </label>
                        </Field>
                        <Field label="Signup label"><input className="ddi-input" value={ads.labels.signup}
                            onChange={(e) => setAds({ ...ads, labels: { ...ads.labels, signup: e.target.value } })}
                            placeholder="abcDEFghi/xyz" data-testid="marketing-ads-label-signup" /></Field>
                        <Field label="Advance payment label"><input className="ddi-input" value={ads.labels.advance_payment}
                            onChange={(e) => setAds({ ...ads, labels: { ...ads.labels, advance_payment: e.target.value } })}
                            placeholder="abcDEFghi/xyz" data-testid="marketing-ads-label-advance" /></Field>
                        <Field label="Full payment label"><input className="ddi-input" value={ads.labels.full_payment}
                            onChange={(e) => setAds({ ...ads, labels: { ...ads.labels, full_payment: e.target.value } })}
                            placeholder="abcDEFghi/xyz" data-testid="marketing-ads-label-full" /></Field>
                    </div>
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <button className="ddi-teal-btn text-sm" disabled={!!busy["save-google_ads"]}
                            onClick={() => save("google_ads", ads)}
                            data-testid="marketing-ads-save">
                            <Save size={13} className="mr-1.5" />
                            {busy["save-google_ads"] ? "Saving…" : "Save"}
                        </button>
                    </div>
                </Card>

                <div className="mt-8 ddi-card ddi-shadow-xs p-4 bg-[#F5F7FB] border-[#3A80F7]/15 flex items-start gap-3">
                    <ShieldCheck size={16} className="text-[#0F766E] mt-0.5 shrink-0" />
                    <div className="text-[13px] text-[#17212B]/80">
                        <span className="font-semibold text-[#123B5D]">Privacy:</span> Every save is audit-logged with the actor and redacted diff — full tokens are never written to logs. First-party tracking beacons only capture anonymised visitor / session IDs and hashed IP; no PII is stored.
                    </div>
                </div>
            </div>
        </AdminShell>
    );
}
